import type { SheetAtom, SheetBox, SheetLayout } from './record-sheet-pages';

// One rounding for every edge: two rows that touch must still touch, or no cut between them is valid.
function boxOf(element: Element, origin: DOMRect): SheetBox {
  const rect = element.getBoundingClientRect();
  return { top: Math.round(rect.top - origin.top), bottom: Math.round(rect.bottom - origin.top) };
}

function pixels(value: string): number {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

// A paragraph wraps inside the control, so its lines are not elements: they are counted from its
// line height, which is fixed, down from where its text starts.
function paragraphLines(textarea: HTMLTextAreaElement, origin: DOMRect): SheetBox[] {
  const style = getComputedStyle(textarea);
  const lineHeight = pixels(style.lineHeight);
  const rect = textarea.getBoundingClientRect();
  if (lineHeight <= 0 || rect.height === 0) return [];
  const paddingTop = pixels(style.paddingTop);
  const textTop = rect.top - origin.top + pixels(style.borderTopWidth) + paddingTop;
  const count = Math.floor(
    (textarea.scrollHeight - paddingTop - pixels(style.paddingBottom)) / lineHeight
  );
  return Array.from({ length: Math.max(count, 0) }, (_, line) => ({
    top: Math.round(textTop + line * lineHeight),
    bottom: Math.round(textTop + (line + 1) * lineHeight),
  }));
}

function tableAtoms(wrapper: HTMLElement, origin: DOMRect): SheetAtom[] {
  const tableId = wrapper.dataset.sheetId;
  // A table nested in a cell has cells of its own; they sit inside this table's cells.
  const cells = [...wrapper.querySelectorAll<HTMLElement>('[data-sheet-cell]')].filter(
    (cell) => cell.dataset.sheetTable === tableId
  );
  const box = boxOf(wrapper, origin);
  if (cells.length === 0) return [{ ...box, kind: 'node' }];

  const cellAtoms: SheetAtom[] = cells.map((cell) => ({
    ...boxOf(cell, origin),
    kind: 'cell',
    tableId,
    cellId: cell.dataset.sheetCell,
  }));
  const fieldTops = cells.flatMap((cell, index) =>
    cell.dataset.sheetField === undefined ? [] : [cellAtoms[index].top]
  );
  const bandRowTop = Math.min(
    ...(fieldTops.length > 0 ? fieldTops : cellAtoms.map((atom) => atom.top))
  );
  const bandBottom = Math.min(
    ...cellAtoms.filter((atom) => atom.top === bandRowTop).map((atom) => atom.bottom)
  );
  // The space above the grid, the title and the labels go with the first row to fill in, or they
  // end a page alone.
  return [{ top: box.top, bottom: bandBottom, kind: 'tableBand', tableId }, ...cellAtoms];
}

export function measureSheet(stage: HTMLElement): SheetLayout {
  const origin = stage.getBoundingClientRect();
  let header: SheetBox | null = null;
  const atoms: SheetAtom[] = [];

  for (const wrapper of stage.querySelectorAll<HTMLElement>('[data-sheet-node]')) {
    const kind = wrapper.dataset.sheetNode;
    if (kind === 'table') {
      atoms.push(...tableAtoms(wrapper, origin));
      continue;
    }
    const box = boxOf(wrapper, origin);
    if (kind === 'header' && header === null) header = box;
    atoms.push({ ...box, kind: 'node' });
  }

  const textLines = [...stage.querySelectorAll('textarea')].flatMap((textarea) =>
    paragraphLines(textarea, origin)
  );
  return { height: Math.round(origin.height), header, atoms, textLines };
}
